import { Card, CardContent, Paper, Stack, Typography } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { ClubOption } from "../clubs/clubApi";
import { ClubBadge } from "../clubs/ClubLogo";
import useClubLogos from "../clubs/useClubLogos";
import { GameOrLessonCard, NoUpcomingEvents, EventsLoading } from "./ClubPreviewCard";
import { EventType } from "./FilterEvents";
import getLessonsByClub from "./getLessonsByClub";

type Setter = ((value: any) => void) | undefined;

type ScheduleEvent = {
  id: number;
  event_type: "lesson" | "game" | "tournament_league";
  raw_date: moment.MomentInput;
  available?: number;
  slots?: number;
  [key: string]: unknown;
};

type ScheduleDay = { day: string; date: string; lessons: ScheduleEvent[] };

type ClubEvent = { club: ClubOption; event: ScheduleEvent };

type MergedDay = { date: string; day: string; events: ClubEvent[] };

const EVENT_FILTER_NAMES: Record<
  ScheduleEvent["event_type"],
  "Lessons" | "Games" | "Tournaments"
> = {
  lesson: "Lessons",
  game: "Games",
  tournament_league: "Tournaments",
};

function fetchProMapping(devPort: string, clubId: number): Promise<Record<number, string>> {
  return fetch(devPort + "player_list/" + clubId + "/True")
    .then((response) => response.json())
    .then((players: [string, string, unknown, unknown, number][] | null) => {
      const mapping: Record<number, string> = {};
      (players || []).forEach((player) => {
        mapping[player[4]] = player[0] + " " + player[1];
      });
      return mapping;
    })
    .catch(() => ({}));
}

function fetchClubDays(
  devPort: string,
  clubId: number,
  startDate: string,
  endDate: string
): Promise<ScheduleDay[]> {
  return fetchProMapping(devPort, clubId).then(
    (proMapping) =>
      new Promise((resolve) =>
        getLessonsByClub(devPort, clubId, resolve, proMapping, startDate, endDate)
      )
  );
}

export function mergeClubDays(perClub: { club: ClubOption; days: ScheduleDay[] }[]): MergedDay[] {
  const byDate: Record<string, MergedDay> = {};
  perClub.forEach(({ club, days }) => {
    days.forEach((day) => {
      if (!byDate[day.date]) {
        byDate[day.date] = { date: day.date, day: day.day, events: [] };
      }
      day.lessons.forEach((event) => byDate[day.date].events.push({ club, event }));
    });
  });
  return Object.values(byDate)
    .sort((a, b) => moment(a.date, "MM-DD-YYYY").valueOf() - moment(b.date, "MM-DD-YYYY").valueOf())
    .map((day) => ({
      ...day,
      events: [...day.events].sort(
        (a, b) => moment(a.event.raw_date).valueOf() - moment(b.event.raw_date).valueOf()
      ),
    }));
}

type AllClubsScheduleProps = {
  devPort: string;
  clubs: ClubOption[];
  playerId: number;
  startDate: string;
  endDate: string;
  selectedEventTypes: EventType[];
  showOnlyBookedLessons: boolean;
  setEventId?: Setter;
  setLessonPro?: Setter;
  setLessonPrice?: Setter;
  setClubId?: Setter;
  setLeasePrice?: Setter;
  setGameDate?: Setter;
  setGamePoloType?: Setter;
  setGameDivisions?: Setter;
  setEventDetails?: Setter;
};

function AllClubsSchedule({
  devPort,
  clubs,
  playerId,
  startDate,
  endDate,
  selectedEventTypes,
  showOnlyBookedLessons,
  ...cardProps
}: AllClubsScheduleProps) {
  const [days, setDays] = useState<MergedDay[] | null>(null);
  const logos = useClubLogos(
    devPort,
    clubs.map((club) => club.value)
  );

  useEffect(() => {
    let cancelled = false;
    setDays(null);
    Promise.all(
      clubs.map((club) =>
        fetchClubDays(devPort, club.value, startDate, endDate).then((clubDays) => ({
          club,
          days: clubDays,
        }))
      )
    ).then((perClub) => {
      if (!cancelled) {
        setDays(mergeClubDays(perClub));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [devPort, clubs, startDate, endDate]);

  const visibleDays = useMemo(() => {
    const selected = new Set(selectedEventTypes.map((eventType) => eventType.type));
    return (days || [])
      .map((day) => ({
        ...day,
        events: day.events.filter(
          ({ event }) =>
            selected.has(EVENT_FILTER_NAMES[event.event_type]) &&
            !(
              showOnlyBookedLessons &&
              event.event_type === "lesson" &&
              event.available === event.slots
            )
        ),
      }))
      .filter((day) => day.events.length > 0);
  }, [days, selectedEventTypes, showOnlyBookedLessons]);

  if (!days) {
    return <EventsLoading />;
  }
  if (visibleDays.length === 0) {
    return <NoUpcomingEvents managerMode={playerId} compact={false} />;
  }

  return (
    <Stack spacing={2} data-testid="all-clubs-schedule">
      {visibleDays.map((day) => (
        <Paper elevation={24} key={day.date} data-testid={"schedule-day-" + day.date}>
          <Card variant="elevation">
            <CardContent>
              <Stack spacing={2}>
                <Typography>{day.day + " " + day.date}</Typography>
                {day.events.map(({ club, event }) => (
                  <GameOrLessonCard
                    key={club.value + "-" + event.event_type + "-" + event.id}
                    lesson_day={day}
                    lesson={event}
                    managerMode={playerId}
                    clubId={club.value}
                    weekAtAGlance={false}
                    selectedEventTypes={selectedEventTypes}
                    showOnlyBookedLessons={showOnlyBookedLessons}
                    clubBadge={
                      <ClubBadge clubId={club.value} name={club.label} logo={logos[club.value]} />
                    }
                    {...cardProps}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Paper>
      ))}
    </Stack>
  );
}

export default AllClubsSchedule;
